<template>
  <div>
    <div class="loading" v-if="isLoading == true">
      <div class="sk-three-bounce">
        <div class="sk-child sk-bounce1"></div>
        <div class="sk-child sk-bounce2"></div>
        <div class="sk-child sk-bounce3"></div>
      </div>
    </div>

    <b-row class="mb-3" v-if="$can('export')">
      <b-col sm>
        <button 
          class="btn btn-success my-2 mr-3" 
          type="button" 
          @click="exportAmlTable"
          :disabled="isAmlExportDisabled" 
        >
          Export
        </button>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col sm="6">
        <b-form-group class="mb-3" label="Search By" label-for="aml-search" description="Partner users are only searchable by NIK">
          <b-input-group>
            <b-input-group-prepend>
              <b-dropdown :text="amlTypeFilter" v-model="amlTypeFilter" variant="secondary">
                <b-dropdown-item v-for="(item, index) in amlTypeData" :key="`aml-search-${index}`" @click="amlTypeFilter = item.value">
                  {{ item.text }}
                </b-dropdown-item>
              </b-dropdown>
            </b-input-group-prepend>
            <b-form-input id="aml-search" type="search" v-model="amlFilterText" placeholder="Name, Email, NIK, Phone ..."></b-form-input>
            <b-input-group-append>
              <b-button variant="secondary" @click="resetAmlFilter" type="button">Reset</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>

      <b-col sm="6">
        <b-form-group
          class="mb-3"
          label="Screening Date"
          label-for="aml-daterange"
          :state="amlDateRangeState === false ? false : amlComputedDateRequiredState"
        >
          <template #invalid-feedback>
            <span v-if="amlDateRangeState === false">The screening date range must be at least 1 hour and maximum 1 year</span>
            <span v-else-if="amlComputedDateRequiredState === false">Screening date is required when using category filters without specific search</span>
          </template>
          <b-input-group>
            <range-picker
              id="aml-daterange"
              :start="amlStartDate"
              :end="amlEndDate"
              :type="'clear'"
              :disable="false"
              :autoUpdate="false"
              :timePicker="true"
              :maxYears="1"
              :name="'aml-daterange'"
              @picker="doAmlDateFilter"
            ></range-picker>
            <b-input-group-append>
              <b-button type="button" variant="secondary" @click="resetAmlDateFilter">Reset</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>

      <b-col sm="6">
        <b-form-group class="mb-3" label="Partner" label-for="aml-partner">
          <b-form-select id="aml-partner" v-model="amlFilterPartner" :options="partnerOptions"></b-form-select>
        </b-form-group>
      </b-col>

      <b-col sm="6">
        <b-form-group class="mb-3" label="Screening AML Status" label-for="aml-status">
          <b-form-select id="aml-status" v-model="amlStatus" :options="amlStatusOptions"></b-form-select>
        </b-form-group>
      </b-col>

      <b-col sm="6">
        <b-form-group class="mb-3" label="Profile Risk" label-for="aml-risk">
          <b-form-select id="aml-risk" v-model="amlProfileRisk" :options="amlProfileRiskOptions"></b-form-select>
        </b-form-group>
      </b-col>

      <b-col sm="12">
        <b-button :disabled="isAmlApplyDisabled" :style="amlButtonStyle" class="mb-2" @click="doApplyAmlFilter">
          Apply Filter
        </b-button>
        <p class="mb-3" :style="{ color: 'rgba(115, 129, 143, 1)' }">Click button 'Apply Filter' to apply the filter</p>
      </b-col>
    </b-row>

    <b-skeleton-table v-if="amlLoadingTable == true" :rows="10" :columns="8" :table-props="{ bordered: true }"></b-skeleton-table>

    <vuetable
      v-else
      ref="vuetableAml"
      :api-mode="false"
      :fields="amlFields"
      :per-page="limit"
      :data-manager="dataManagerAml"
      :no-data-template="isAmlParams ? defaultTemplate : noDataTemplate"
    >
      <template slot="aml-name-slot" slot-scope="prop">
        <span>{{ amlUserName(prop.rowData) }}&nbsp;({{ amlUserEmail(prop.rowData) }})</span>
      </template>
      <template slot="aml-partner-slot" slot-scope="prop">
        <span>{{ prop.rowData.partner_name || "Treasury" }}</span>
      </template>
      <template slot="aml-nik-slot" slot-scope="prop">
        <span>{{ amlNik(prop.rowData) }}</span>
      </template>
      <template slot="aml-score-slot" slot-scope="prop">
        <span>{{ amlScore(prop.rowData) }}</span>
      </template>
      <template slot="aml-risk-slot" slot-scope="prop">
        <span>{{ amlRisk(prop.rowData) }}</span>
      </template>
      <template slot="aml-occupation-slot" slot-scope="prop">
        <span>{{ amlOccupation(prop.rowData) }}</span>
      </template>
      <template slot="aml-actions-slot" slot-scope="prop">
        <div class="custom-actions" v-if="$can('list_aml')">
          <button type="button" class="btn btn-info m-1 text-white" @click="onAmlAction('detail', prop.rowData)">Detail</button>
          <button
            type="button"
            class="btn btn-warning m-1"
            :disabled="amlLoadingLog"
            @click="onAmlAction('log', prop.rowData)"
          >
            {{ amlLoadingLog && amlLoadingLogId === prop.rowData.id ? "Loading..." : "LOG" }}
          </button>
        </div>
      </template>
    </vuetable>

    <div v-show="isAmlParams" class="vuetable-pagination ui basic segment grid">
      <div class="wrapper-pagination-custom">
        <div class="pagination-custom-info"><p>{{ this.amlPaginationInfo }}</p></div>
        <div class="pagination-custom-button">
          <button :disabled="amlCurrentPage === 1" @click="doMoveAmlPage('first')">
            <i v-if="amlCurrentPage === 1" class="fa fa-angle-double-left disabled"></i>
            <i v-else class="fa fa-angle-double-left"></i>
          </button>
          <button :disabled="amlCurrentPage === 1" @click="doMoveAmlPage('prev')">
            <i v-if="amlCurrentPage === 1" class="fa fa-angle-left disabled"></i>
            <i v-else class="fa fa-angle-left"></i>
          </button>
          <div>{{ this.amlCurrentPage }}</div>
          <button :disabled="amlData.length < 10" @click="doMoveAmlPage('next')">
            <i v-if="amlData.length < 10" class="fa fa-angle-right disabled"></i>
            <i v-else class="fa fa-angle-right"></i>
          </button>
        </div>
      </div>
    </div>

    <b-modal :title="amlLogTitle" size="lg" v-model="amlLogModal" centered hide-footer>
      <div class="wrapper-log-detail" v-if="amlLogs.length === 0"><p>Data Belum Tersedia!</p></div>
      <div v-else>
        <b-card no-body v-for="(item, index) in amlLogs" :key="item.id || index" class="mb-2">
          <b-card-header header-tag="header" class="p-1 aml-log-header" role="tab">
            <b-button block v-b-toggle="'aml-log-' + index" class="text-left font-weight-bold aml-collapse-toggle" variant="light">
              AML Screening Process - {{ amlLogMonthYear(item) }}
            </b-button>
          </b-card-header>
          <b-collapse :id="'aml-log-' + index" class="p-2">
            <div class="aml-attachment-wrapper">
              <pre class="aml-attachment-content">{{ formatAmlAttachment(item.attachment) }}</pre>
            </div>
          </b-collapse>
        </b-card>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import Vuetable from "vuetable-2/src/components/Vuetable";
import _ from "lodash";
import moment from "moment";

Vue.use(Vuetable);

export default {
  name: "AmlVerificationTab",
  props: {
    partnerOptions: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  components: { Vuetable },
  data() {
    return {
      limit: 10,
      amlData: [],
      amlPaginationInfo: "",
      amlFilterText: "",
      amlTypeFilter: "Name",
      amlFilterPartner: "",
      amlStatus: "",
      amlProfileRisk: "",
      amlStartDate: "",
      amlEndDate: "",
      amlDateRangeState: null,
      amlLoadingTable: false,
      amlLoadingApply: false,
      amlLoadingExport: false,
      isLoading: false,
      amlLoadingLog: false,
      amlLoadingLogId: null,
      amlPage: 1,
      amlCurrentPage: 1,
      amlSort: "rs_datetime|desc",
      amlLogModal: false,
      amlLogs: [],
      amlLogTitle: "AML Logs",
      amlTypeData: [
        { value: "Name", text: "Name" },
        { value: "Email", text: "Email" },
        { value: "NIK", text: "NIK" },
        { value: "Phone", text: "Phone" },
      ],
      amlStatusOptions: [
        { value: "", text: "Choose Screening AML Status" },
        { value: "screened", text: "Screened" },
        { value: "not-eligible", text: "Not Screened" },
      ],
      amlProfileRiskOptions: [
        { value: "", text: "Choose Profile Risk" },
        { value: "3", text: "High" },
        { value: "2", text: "Medium" },
        { value: "1", text: "Low" },
      ],
      amlMoreParams: {},
      amlFields: [
        { name: "rs_datetime", sortField: "rs_datetime", title: "Screening Date", formatter: this.amlDateValue },
        { name: "aml-name-slot", sortField: "name", title: "Name" },
        { name: "aml-partner-slot", sortField: "partner_name", title: "Partner/Organization" },
        { name: "aml-nik-slot", sortField: "nik", title: "NIK" },
        { name: "aml-score-slot", sortField: "scoring_aml", title: "Scoring AML" },
        { name: "aml-risk-slot", sortField: "profile_risk", title: "Profile Risk" },
        { name: "aml-occupation-slot", sortField: "occupation_category", title: "Occupation Category" },
        { name: "aml-actions-slot", title: "Actions", titleClass: "center aligned", dataClass: "center aligned" },
      ],
      noDataTemplate:
        '<div class="text-center d-flex align-items-center justify-content-center" style="height: 120px;"><p>Use filter to show your data.</p></div>',
      defaultTemplate:
        '<div class="text-center d-flex align-items-center justify-content-center flex-column" style="height: 120px;"><p><strong>No results found.</strong></p><p>Please adjust your filter or search keywords</p></div>',
      errors: { code: "", message: "", status: "" },
    };
  },
  watch: {
    amlData() {
      this.$refs.vuetableAml?.refresh();
    },
  },
  computed: {
    amlComputedDateRequiredState() {
      const hasSpecificFilter = String(this.amlFilterText || "").trim() !== "";
      const hasCategoryFilter =
        String(this.amlFilterPartner || "").trim() !== "" ||
        String(this.amlStatus || "").trim() !== "" ||
        String(this.amlProfileRisk || "").trim() !== "";
      const dateRangeEmpty = String(this.amlStartDate || "").trim() === "" && String(this.amlEndDate || "").trim() === "";
      if (hasSpecificFilter) return null;
      if (hasCategoryFilter && dateRangeEmpty) return false;
      return null;
    },
    isAmlApplyDisabled() {
      const hasSpecificFilter = String(this.amlFilterText || "").trim() !== "";
      const hasCategoryFilter =
        String(this.amlFilterPartner || "").trim() !== "" ||
        String(this.amlStatus || "").trim() !== "" ||
        String(this.amlProfileRisk || "").trim() !== "";
      const hasDateFilter = String(this.amlStartDate || "").trim() !== "" || String(this.amlEndDate || "").trim() !== "";

      if (this.amlLoadingApply || this.amlLoadingTable) return true;
      if (!hasSpecificFilter && !hasCategoryFilter && !hasDateFilter) return true;
      if (this.amlDateRangeState === false) return true;
      if (hasSpecificFilter) return false;
      if (hasCategoryFilter && !hasDateFilter) return true;
      return false;
    },
    amlButtonStyle() {
      return {
        background: !this.isAmlApplyDisabled ? "#7565F6" : "#c8ced3",
        color: !this.isAmlApplyDisabled ? "#FFFFFF" : "inherit",
      };
    },
    isAmlParams() {
      const params = Object.entries(this.amlMoreParams).filter((entry) => entry[1]).length == 0;
      return !params;
    },
    isAmlExportDisabled() {
      if (this.amlLoadingExport) return true;
      const hasStart = this.amlMoreParams && String(this.amlMoreParams.start_from || "").trim() !== "";
      const hasEnd = this.amlMoreParams && String(this.amlMoreParams.end_to || "").trim() !== "";
      const mandatory = String(this.amlStartDate || "").trim() !== "" && 
        String(this.amlEndDate || "").trim() !== "";
      return !(mandatory && hasStart && hasEnd);
    },
  },
  methods: {
    handleLoadError(error) {
      this.errors.code = error.response.data.meta.code;
      this.errors.message = error.response.data.meta.message;
      this.errors.status = error.response.data.meta.code;
      if (this.errors.code == 401) this.$router.push("/login");
      else if (this.errors.code == 403) this.$router.push("/403");
      else if (this.errors.code == 500) this.$router.push("/500");
      else this.$swal.fire("Error!", this.errors.message || "Something went wrong. Please try again later.", "error");
    },
    onFetchAml() {
      this.amlLoadingTable = true;
      this.amlData = [];
      const newParams = Object.entries(this.amlMoreParams)
        .filter((entry) => entry[1] !== "" && entry[1] !== null && entry[1] !== undefined)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      this.$http
        .get(`aml?page=${this.amlPage}&per_page=${this.limit}${newParams ? `&${newParams}` : ""}`)
        .then((response) => {
          const data = response.data.data || [];
          const startIndex = (response.data.current_page - 1) * this.limit;
          const endIndex = startIndex + this.limit;
          this.amlData = data;
          this.amlCurrentPage = response.data.current_page;
          this.amlPaginationInfo = data.length > 0 ? `Displaying ${startIndex + 1} to ${endIndex}` : "No relevant data";
        })
        .catch((err) => {
          this.handleLoadError(err);
        })
        .finally(() => {
          this.amlLoadingTable = false;
          this.amlLoadingApply = false;
        });
    },
    doApplyAmlFilter() {
      if (this.amlLoadingApply || this.amlLoadingTable) return;
      this.amlLoadingApply = true;
      this.amlPage = 1;
      this.amlMoreParams = {
        partner: this.amlFilterPartner,
        filter: this.amlFilterText ? encodeURIComponent(this.amlFilterText) : "",
        type_filter: this.amlFilterText ? this.amlTypeFilter : "",
        start_from: this.amlStartDate,
        end_to: this.amlEndDate,
        status: this.amlStatus,
        profile_risk: this.amlProfileRisk,
        sort: this.amlSort,
      };
      this.onFetchAml();
    },
    resetAmlFilter() {
      this.amlFilterText = "";
    },
    exportAmlTable() {
      if (this.amlLoadingExport) return;
      this.amlLoadingExport = true;
      this.isLoading = true;
      const startDate = String(this.amlMoreParams.start_from || this.amlStartDate || "").trim();
      const endDate = String(this.amlMoreParams.end_to || this.amlEndDate || "").trim();
      const filterText = String(this.amlFilterText || "").trim();
      const encodedFilterText = filterText ? encodeURIComponent(filterText) : this.amlMoreParams.filter || "";
      const typeFilter = encodedFilterText ? this.amlTypeFilter || this.amlMoreParams.type_filter || "" : "";

      if ((startDate && !endDate) || (!startDate && endDate)) {
        this.amlLoadingExport = false;
        this.isLoading = false;
        this.$swal.fire("Warning!", "Please select complete screening date range.", "warning");
        return;
      }

      const exportParams = {
        partner: this.amlFilterPartner || this.amlMoreParams.partner || "",
        filter: encodedFilterText,
        type_filter: typeFilter,
        status: this.amlStatus || this.amlMoreParams.status || "",
        profile_risk: this.amlProfileRisk || this.amlMoreParams.profile_risk || "",
        sort: this.amlSort || this.amlMoreParams.sort || "",
      };

      const params = Object.entries(exportParams)
        .filter(([, value]) => value !== "" && value !== null && value !== undefined)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");
      const exportEndpoint = startDate && endDate ? `aml-export/${encodeURIComponent(startDate)}/${encodeURIComponent(endDate)}` : "aml-export";

      this.$http
        .get(`${exportEndpoint}${params ? `?${params}` : ""}`)
        .then((result) => {
          const exportPath =
            (result.data.meta && result.data.meta.data) ||
            (result.data.data && result.data.data.path) ||
            result.data.path;
          if (exportPath) window.location.href = exportPath;
        })
        .catch((error) => {
          this.handleLoadError(error);
        })
        .finally(() => {
          this.amlLoadingExport = false;
          this.isLoading = false;
        });
    },
    doMoveAmlPage(value) {
      if (value === "first") this.amlPage = 1;
      else if (value === "prev") this.amlPage = Math.max(1, this.amlPage - 1);
      else this.amlPage++;
      this.onFetchAml();
    },
    doAmlDateFilter(value) {
      this.onAmlDateSet(value);
    },
    onAmlDateSet(value) {
      const startDate = moment(value.startDate);
      const endDate = moment(value.endDate);
      const hoursDiff = endDate.diff(startDate, "hours", true);
      const yearsDiff = endDate.diff(startDate, "years", true);
      if (hoursDiff < 1 || yearsDiff > 1) {
        this.amlDateRangeState = false;
        return;
      }
      this.amlDateRangeState = null;
      this.amlStartDate = startDate.format("YYYY-MM-DD HH:mm:ss");
      this.amlEndDate = endDate.format("YYYY-MM-DD HH:mm:ss");
    },
    resetAmlDateFilter() {
      this.amlStartDate = "";
      this.amlEndDate = "";
      this.amlDateRangeState = null;
    },
    dataManagerAml(sortOrder, pagination) {
      let local = this.amlData;
      if (sortOrder.length > 0) {
        const newSort = `${sortOrder[0].sortField}|${sortOrder[0].direction}`;
        if (this.amlSort !== newSort) {
          this.amlSort = newSort;
          this.amlMoreParams = { ...this.amlMoreParams, sort: this.amlSort };
          this.onFetchAml();
        }
      }
      pagination = this.$refs.vuetableAml.makePagination(local.length, this.limit);
      let from = pagination.from - 1;
      let to = from + this.limit;
      return { pagination: pagination, data: local.length > 0 ? _.slice(local, from, to) : [] };
    },
    onAmlAction(action, rowData) {
      if (!rowData || !rowData.id) return;
      if (action === "detail") {
        const routeData = this.$router.resolve({
          name: "Detail Profile Risk AML",
          params: { id: btoa(rowData.id) },
        });
        window.open(routeData.href, "_blank");
      } else if (action === "log") {
        if (this.amlLoadingLog) return;
        this.amlLoadingLog = true;
        this.amlLoadingLogId = rowData.id;
        this.$http
          .get(`aml/${rowData.id}/logs`)
          .then((result) => {
            const logs = Array.isArray(result.data) ? result.data : (result.data && result.data.data) || [];
            this.amlLogs = logs;
            this.amlLogTitle = `Log Detail (${this.amlUserName(rowData)} - ${this.amlUserEmail(rowData)})`;
            this.amlLogModal = true;
          })
          .catch((error) => this.handleLoadError(error))
          .finally(() => {
            this.amlLoadingLog = false;
            this.amlLoadingLogId = null;
          });
      }
    },
    amlLogMonthYear(item) {
      if (!item) return "-";
      const rsDatetime = item.attachment && item.attachment.rs_datetime;
      const sourceDate = rsDatetime || item.created_at;
      if (!sourceDate) return "-";
      const parsed = moment(sourceDate);
      return parsed.isValid() ? parsed.format("MM/YYYY") : "-";
    },
    formatAmlAttachment(attachment) {
      if (!attachment) return "-";
      try {
        return JSON.stringify(attachment, null, 2);
      } catch (error) {
        return "-";
      }
    },
    amlDateValue(value) { return value || "-"; },
    amlUserName(rowData) { return rowData.name || rowData.full_name || "-"; },
    amlUserEmail(rowData) { return rowData.email || "-"; },
    amlNik(rowData) { return rowData.nik || rowData.ktp || rowData.id_number || "-"; },
    amlScore(rowData) { return rowData.scoring_aml || rowData.aml_score || rowData.score || "-"; },
    amlRisk(rowData) { return rowData.profile_risk || rowData.risk_profile || "-"; },
    amlOccupation(rowData) { return rowData.occupation_category || rowData.occupation || "-"; },
  },
};
</script>

<style>
.loading {
  position: fixed;
  z-index: 999;
  height: 2em;
  width: 2em;
  overflow: visible;
  margin: auto;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.loading:before {
  content: "";
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
}

.wrapper-log-detail {
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wrapper-pagination-custom {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f9fafb;
}

.pagination-custom-button { display: flex; }

.pagination-custom-button button {
  width: 28px;
  height: 47px;
  padding: 13px 16px;
  font-size: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: solid rgba(0, 0, 0, 0.3);
}

.pagination-custom-button button:first-child {
  padding: 13px 16px 13px 30px;
  border-radius: 4px 0 0 4px;
  border-width: 1px 0 1px 1px;
}

.pagination-custom-button button:nth-child(2),
.pagination-custom-button button:last-child {
  font-size: 1.3em;
  border-width: 1px;
}

.pagination-custom-button button:last-child { border-radius: 0 4px 4px 0; }
.pagination-custom-button button i.disabled { opacity: 0.6; }

.pagination-custom-button div {
  width: 28px;
  height: 47px;
  font-size: 1em;
  font-weight: 500;
  background: #ffffff;
  border: solid rgba(0, 0, 0, 0.3);
  border-width: 1px 0 1px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.aml-log-header { background-color: #f0f3f5; }

.aml-attachment-wrapper {
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 12px;
}

.aml-attachment-content {
  margin: 0;
  max-height: 300px;
  overflow: auto;
  color: #606060;
  white-space: pre-wrap;
  word-break: break-word;
}

.aml-collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.aml-collapse-toggle::after {
  content: "\f106";
  font-family: FontAwesome;
  color: #606060;
  font-size: 14px;
}

.aml-collapse-toggle.collapsed::after { content: "\f107"; }
</style>
<style src="spinkit/scss/spinkit.scss" lang="scss" />